import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate, ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'

import {
    Card,
    CardContent,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Switch
} from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import { RegraRow } from '../../regra/regra.types'
import AnaliseFormScope from '../analise_form.scopes'

const LOG = Logger.get('ANALISE-FORM-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    switch: {
        zoom: 0.6
    },
    hbox: {
        display: 'flex',
        flexDirection: 'row'
    },
    vbox: {
        display: 'flex',
        flexDirection: 'column'
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type AnaliseFormViewProps = {
    className?: string
    scope: AnaliseFormScope
}
class AnaliseFormViewClass implements FCClassContext<AnaliseFormViewProps> {
    // :: emissors

    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly handleAddRegraClick = () => {
        this.scope.onAddRegra().catch(LOG.caught)
    }

    readonly handleEditRegraClick = (id: GridRowId) => () => {
        this.scope.idRegraSelecionada = id as number
        this.scope.onEditRegra().catch(LOG.caught)
    }

    readonly handleDeleteRegraClick = (id: GridRowId) => () => {
        this.scope.idRegraSelecionada = id as number
        this.scope.onDeleteRegra().catch(LOG.caught)
    }

    #handleChangeAtivoCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.scope.status = e.target.checked ? 'ATIVO' : 'INATIVO'
    }

    #handleTipoChange = (e: SelectChangeEvent) => {
        this.scope.tipo = e.target.value as string
    }

    #handleModeloSerasaChange = (e: SelectChangeEvent) => {
        this.scope.modeloSerasa = e.target.value as string
    }

    // :: Fields
    scope!: AnaliseFormScope
    sytles!: ReturnType<typeof useStyles>
    regraColumns = static_build_regra_columns(this.handleEditRegraClick, this.handleDeleteRegraClick)

    // :: Methods

    onSyncState({ scope }: AnaliseFormViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: AnaliseFormViewProps) {
        this.scope = bindUpdate(React, scope)

        const classes = this.sytles.classes

        if (scope.waiting) {
            return <ViewSlot scope={scope.waiting} />
        }

        return (
            <div className={clsx(classes.view, className)}>
                <Paper elevation={3} style={{ padding: 10 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginBottom: '20px'
                        }}
                    >
                        <FormControl>
                            <InputLabel id="tipo-select-label">Tipo</InputLabel>
                            <Select
                                labelId="tipo-select-label"
                                id="tipo-select"
                                value={scope.tipo}
                                label="Tipo"
                                onChange={this.#handleTipoChange}
                            >
                                <MenuItem value={'CEDENTE_SACADO'}>Cedente/Sacado</MenuItem>
                                <MenuItem value={'CEDENTE'}>Cedente</MenuItem>
                                <MenuItem value={'SACADO'}>Sacado</MenuItem>
                                <MenuItem value={'TITULO'}>Título</MenuItem>
                                <MenuItem value={'NFE'}>Nfe</MenuItem>
                                <MenuItem value={'LOTE_CNPJ_CPF'}>Lote CNPJ/CPF</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            className={classes.cnpjInput}
                            value={scope.nome}
                            onChange={(e) => (scope.nome = e.target.value)}
                            label="Nome da análise"
                            variant="standard"
                        />

                        <div className={classes.hbox}>
                            <FormGroup className={classes.vbox}>
                                <FormControlLabel
                                    label={scope.status === 'ATIVO' ? 'Ativo' : 'Inativo'}
                                    control={
                                        <Switch
                                            checked={scope.status === 'ATIVO'}
                                            onChange={this.#handleChangeAtivoCheck}
                                        />
                                    }
                                />
                            </FormGroup>
                        </div>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginBottom: '20px'
                        }}
                    >
                        <TextField
                            className={classes.cnpjInput}
                            value={scope.riscoBorderoDe}
                            onChange={(e) => (scope.riscoBorderoDe = Number(e.target.value ?? 0))}
                            label="Risco Borderô de"
                            variant="standard"
                        />
                        <TextField
                            className={classes.cnpjInput}
                            value={scope.riscoBorderoAte}
                            onChange={(e) => (scope.riscoBorderoAte = Number(e.target.value ?? 0))}
                            label="Risco Borderô até"
                            variant="standard"
                        />
                        <TextField
                            className={classes.cnpjInput}
                            value={scope.riscoCarteiraDe}
                            onChange={(e) => (scope.riscoCarteiraDe = Number(e.target.value ?? 0))}
                            label="Risco Carteira de"
                            variant="standard"
                        />
                        <TextField
                            className={classes.cnpjInput}
                            value={scope.riscoCarteiraAte}
                            onChange={(e) => (scope.riscoCarteiraAte = Number(e.target.value ?? 0))}
                            label="Risco Carteira até"
                            variant="standard"
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginBottom: '20px'
                        }}
                    >
                        <FormGroup className={classes.hbox}>
                            <FormControlLabel
                                required
                                label="Parar ao bloquear"
                                control={
                                    <Checkbox
                                        checked={scope.pararAoBloquear}
                                        onChange={(e) => (this.scope.pararAoBloquear = e.target.checked)}
                                    />
                                }
                            />
                        </FormGroup>

                        <FormGroup className={classes.hbox}>
                            <FormControlLabel
                                required
                                label="Obrigatório NFe"
                                control={
                                    <Checkbox
                                        checked={scope.obrigatorioNfe}
                                        onChange={(e) => (this.scope.obrigatorioNfe = e.target.checked)}
                                    />
                                }
                            />
                        </FormGroup>

                        <FormGroup className={classes.hbox}>
                            <FormControlLabel
                                required
                                label="Aplicar PF"
                                control={
                                    <Checkbox
                                        checked={scope.aplicarPf}
                                        onChange={(e) => (this.scope.aplicarPf = e.target.checked)}
                                    />
                                }
                            />
                        </FormGroup>

                        <Card variant="outlined">
                            <CardContent>
                                <FormGroup className={classes.vbox}>
                                    <FormControl sx={{ margin: 1 }}>
                                        <InputLabel id="modelo-serasa-select-label">Modelo Serasa</InputLabel>
                                        <Select
                                            labelId="modelo-serasa-select-label"
                                            id="modelo-serasa-select"
                                            defaultValue={scope.modeloSerasa}
                                            label="Tipo"
                                            onChange={this.#handleModeloSerasaChange}
                                        >
                                            <MenuItem value={'RELATO_QSA'}>Relato QSA</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        sx={{ margin: 1 }}
                                        className={classes.cnpjInput}
                                        value={scope.consultarSerasaDias}
                                        onChange={(e) => (scope.consultarSerasaDias = Number(e.target.value ?? 0))}
                                        label="Consultar Serasa dias"
                                        variant="standard"
                                    />
                                    <FormControlLabel
                                        required
                                        sx={{ margin: 1 }}
                                        label="Não realizar nova consulta Serasa"
                                        control={
                                            <Checkbox
                                                checked={scope.naoRealizarNovaConsulta}
                                                onChange={(e) =>
                                                    (this.scope.naoRealizarNovaConsulta = e.target.checked)
                                                }
                                            />
                                        }
                                    />
                                </FormGroup>
                            </CardContent>
                        </Card>

                        <Card variant="outlined">
                            <CardContent>
                                <FormGroup className={classes.vbox}>
                                    <TextField
                                        sx={{ margin: 1 }}
                                        className={classes.cnpjInput}
                                        value={scope.consultarProtestosDias}
                                        onChange={(e) => (scope.consultarProtestosDias = Number(e.target.value ?? 0))}
                                        label="Consultar Protestos dias"
                                        variant="standard"
                                    />
                                    <FormControlLabel
                                        required
                                        sx={{ margin: 1 }}
                                        label="Não realizar nova consulta de protestos"
                                        control={
                                            <Checkbox
                                                checked={scope.naoRealizarNovaConsultaProtestos}
                                                onChange={(e) =>
                                                    (this.scope.naoRealizarNovaConsultaProtestos = e.target.checked)
                                                }
                                            />
                                        }
                                    />
                                </FormGroup>
                            </CardContent>
                        </Card>

                        <Card variant="outlined">
                            <CardContent>
                                <FormGroup className={classes.vbox}>
                                    <TextField
                                        sx={{ margin: 1 }}
                                        className={classes.cnpjInput}
                                        value={scope.consultarSituacaoPfDias}
                                        onChange={(e) => (scope.consultarSituacaoPfDias = Number(e.target.value ?? 0))}
                                        label="Consultar situação de PF dias"
                                        variant="standard"
                                    />
                                    <FormControlLabel
                                        required
                                        sx={{ margin: 1 }}
                                        label="Não realizar nova consulta de situação de PF"
                                        control={
                                            <Checkbox
                                                checked={scope.naoRealizarNovaConsultaSituacaoPf}
                                                onChange={(e) =>
                                                    (this.scope.naoRealizarNovaConsultaSituacaoPf = e.target.checked)
                                                }
                                            />
                                        }
                                    />
                                </FormGroup>
                            </CardContent>
                        </Card>
                    </Box>

                    {this.scope.idSelected > 0 && (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'baseline',
                                    gap: '10px',
                                    marginTop: '10px'
                                }}
                            >
                                <Button variant="outlined" onClick={this.handleAddRegraClick}>
                                    Adicionar Regra
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    minHeight: 100,
                                    flexDirection: 'row',
                                    alignItems: 'baseline',
                                    gap: '10px',
                                    marginTop: '10px',
                                    marginBottom: '20px'
                                }}
                            >
                                <DataGrid
                                    autoHeight={this.scope.regras.length === 0}
                                    rows={this.scope.regras}
                                    columns={this.regraColumns}
                                    getRowId={static_getRegraRowId}
                                    initialState={{
                                        pagination: {
                                            paginationModel: {
                                                pageSize: 10
                                            }
                                        }
                                    }}
                                />
                            </Box>
                        </>
                    )}

                    <Divider />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginTop: '10px'
                        }}
                    >
                        <Button variant="outlined" onClick={this.emitOnSave} disabled={this.scope.saving}>
                            Salvar
                        </Button>
                        <Button variant="outlined" onClick={this.emitOnClose}>
                            Voltar
                        </Button>
                    </Box>
                </Paper>
            </div>
        )
    }
}

export const AnaliseFormView = classToFComponent(AnaliseFormViewClass, React)
export default AnaliseFormView

function static_build_regra_columns(
    handleEditClick: (id: GridRowId) => () => void,
    handleDeleteClick: (id: GridRowId) => () => void
): GridColDef[] {
    return [
        {
            field: 'acao',
            headerName: 'Ação',
            width: 100,
            valueFormatter: (value: string) => {
                switch (value) {
                    case 'BLOQUEAR':
                        return 'Bloquear'
                    case 'ALERTAR':
                        return 'Alertar'
                    case 'LIBERAR':
                        return 'Liberar'
                    default:
                        return ''
                }
            }
        },
        {
            field: 'nome',
            headerName: 'Nome',
            width: 300
        },
        {
            field: 'rating1',
            headerName: 'Rating 1',
            width: 100
        },
        {
            field: 'rating2',
            headerName: 'Rating 2',
            width: 100
        },
        {
            field: 'enviarNotificacao',
            headerName: 'Enviar Notificação',
            width: 100,
            valueFormatter: (value: string) => {
                return value ? 'Sim' : 'Não'
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        key={'edit' + id}
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={'del' + id}
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                ]
            }
        }
    ]
}

function static_getRegraRowId(row: RegraRow) {
    return row.id + ''
}
